import { useState } from 'react'

import { getEmailErrorMessage } from '../../utils/validation'

export interface UseEntityNoticeEmailContinueOptions {
  onSubmit?: (email: string) => void
  /** Runs once, right before the e-mail is validated — e.g. persisting a WhatsApp phone. */
  onBeforeValidate?: () => void
}

export interface UseEntityNoticeEmailContinueReturn {
  isEmailVisible: boolean
  emailValue: string
  emailError: string | undefined
  revealEmail: () => void
  setEmailValue: (value: string) => void
  clearEmailError: () => void
  handleContinue: () => void
}

/**
 * The e-mail step machine shared by every variant's shape: no field visible
 * → reveal on first "continue" (or on picking the e-mail option) → validate
 * and submit on the next one. Kept separate from render so
 * `EntityNoticeEntryEmailFirst` and `EntityNoticeEntryPicker` drive the same
 * machine without duplicating the transition logic.
 */
export function useEntityNoticeEmailContinue({
  onSubmit,
  onBeforeValidate,
}: UseEntityNoticeEmailContinueOptions): UseEntityNoticeEmailContinueReturn {
  const [isEmailVisible, setIsEmailVisible] = useState(false)
  const [emailValue, setEmailValue] = useState('')
  const [emailError, setEmailError] = useState<string | undefined>(undefined)

  const revealEmail = () => setIsEmailVisible(true)
  const clearEmailError = () => setEmailError(undefined)

  const handleContinue = () => {
    if (!isEmailVisible) {
      revealEmail()
      return
    }

    onBeforeValidate?.()

    const validationError = getEmailErrorMessage(emailValue)
    if (validationError) {
      setEmailError(validationError)
      return
    }

    setEmailError(undefined)
    onSubmit?.(emailValue)
  }

  return {
    isEmailVisible,
    emailValue,
    emailError,
    revealEmail,
    setEmailValue,
    clearEmailError,
    handleContinue,
  }
}
